"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.shouldDisplayBankLinkedSuccessParam = exports.hasBankDetectionFlow = exports.isStripePendingVerificationForCountry = exports.getBankAccountFields = exports.getBankAccountDefaultValues = exports.getFullRoutingNumber = exports.getOnboardingBankSteps = exports.getAccountNumberInvalidErrorKey = exports.getBankAccountNumberRules = exports.isDeIbanValid = exports.isIeIbanValid = exports.sanitizeBankAccountNumber = exports.sanitizeDigitsOnly = exports.bankAccountNumberInputMode = exports.bankAccountNumberAutoComplete = exports.bankAccountNumberPlaceholder = void 0;
const country_1 = require("../enums/country");
const bankAccountNumberPlaceholder = (country) => {
    if (country === country_1.OrganizationCountry.Australia) {
        return "000123456";
    }
    if (country === country_1.OrganizationCountry.GreatBritain) {
        return "12345678";
    }
    if (country === country_1.OrganizationCountry.Ireland) {
        return "IE29AIBK93115212345678";
    }
    if (country === country_1.OrganizationCountry.Germany) {
        return "DE89370400440532013000";
    }
    return "000123456789";
};
exports.bankAccountNumberPlaceholder = bankAccountNumberPlaceholder;
const bankAccountNumberAutoComplete = (country) => {
    return country === country_1.OrganizationCountry.UnitedStates ? "off" : undefined;
};
exports.bankAccountNumberAutoComplete = bankAccountNumberAutoComplete;
const bankAccountNumberInputMode = (country) => {
    return country === country_1.OrganizationCountry.UnitedStates || country === country_1.OrganizationCountry.Australia
        ? "numeric"
        : undefined;
};
exports.bankAccountNumberInputMode = bankAccountNumberInputMode;
const sanitizeDigitsOnly = (value, options = { preserveLeadingZero: true }) => {
    const digitsOnly = value.replace(/[^0-9]/g, "");
    if (options.preserveLeadingZero) {
        return digitsOnly;
    }
    return digitsOnly.replace(/^0+/, "") || "0";
};
exports.sanitizeDigitsOnly = sanitizeDigitsOnly;
const sanitizeBankAccountNumber = (country, value) => {
    if (country === country_1.OrganizationCountry.UnitedStates) {
        return (0, exports.sanitizeDigitsOnly)(value, { preserveLeadingZero: true });
    }
    if (country === country_1.OrganizationCountry.Ireland || country === country_1.OrganizationCountry.Germany) {
        return value.replace(/\s+/g, "").toUpperCase();
    }
    return value;
};
exports.sanitizeBankAccountNumber = sanitizeBankAccountNumber;
const isIeIbanValid = (iban) => {
    return /^IE\d{2}[A-Z]{4}\d{14}$/.test(iban.replace(/\s+/g, "").toUpperCase());
};
exports.isIeIbanValid = isIeIbanValid;
const isDeIbanValid = (iban) => {
    return /^DE\d{2}\d{8}\d{10}$/.test(iban.replace(/\s+/g, "").toUpperCase());
};
exports.isDeIbanValid = isDeIbanValid;
const hasOnlyNumbersRule = {
    name: "hasOnlyNumbers",
    messageKey: "createBankAccount.accountNumberOnlyDigits",
    isValid: (value) => /^\d+$/.test(value),
};
const usAccountNumberLengthRule = {
    name: "isAccountNumberLengthValidUs",
    messageKey: "createBankAccount.accountNumberInvalidLengthUs",
    isValid: (value) => value.length >= 4 && value.length <= 17,
};
const auAccountNumberLengthRule = {
    name: "isAccountNumberLengthValidAu",
    messageKey: "createBankAccount.accountNumberInvalidLengthAu",
    isValid: (value) => value.length >= 6 && value.length <= 10,
};
const ieIbanRule = {
    name: "isIbanValidIe",
    messageKey: "createBankAccount.accountNumberInvalidIbanIe",
    isValid: exports.isIeIbanValid,
};
const deIbanRule = {
    name: "isIbanValidDe",
    messageKey: "createBankAccount.accountNumberInvalidIbanDe",
    isValid: exports.isDeIbanValid,
};
const getBankAccountNumberRules = (country) => {
    if (country === country_1.OrganizationCountry.UnitedStates) {
        return [hasOnlyNumbersRule, usAccountNumberLengthRule];
    }
    if (country === country_1.OrganizationCountry.Australia) {
        return [hasOnlyNumbersRule, auAccountNumberLengthRule];
    }
    if (country === country_1.OrganizationCountry.Ireland) {
        return [ieIbanRule];
    }
    if (country === country_1.OrganizationCountry.Germany) {
        return [deIbanRule];
    }
    return [hasOnlyNumbersRule];
};
exports.getBankAccountNumberRules = getBankAccountNumberRules;
const getAccountNumberInvalidErrorKey = (country) => {
    if (country === country_1.OrganizationCountry.GreatBritain) {
        return "bankAccountStep.error.sortCodeAccountNumberMismatch_gb";
    }
    const countrySpecificRule = country
        ? (0, exports.getBankAccountNumberRules)(country).find(({ name }) => name !== "hasOnlyNumbers")
        : undefined;
    return countrySpecificRule?.messageKey ?? "bankAccountStep.error.accountNumberInvalid";
};
exports.getAccountNumberInvalidErrorKey = getAccountNumberInvalidErrorKey;
const getOnboardingBankSteps = (country) => {
    if (!country) {
        return [];
    }
    if (country === country_1.OrganizationCountry.Canada) {
        return [
            "bank.onboardingStepper.step.bank-account.transit",
            "bank.onboardingStepper.step.bank-account.institution",
            "bank.onboardingStepper.step.bank-account.account",
        ];
    }
    if (country === country_1.OrganizationCountry.Ireland || country === country_1.OrganizationCountry.Germany) {
        return ["bank.onboardingStepper.step.bank-account.account"];
    }
    return ["bank.onboardingStepper.step.bank-account.account", "bank.onboardingStepper.step.bank-account.routing"];
};
exports.getOnboardingBankSteps = getOnboardingBankSteps;
// Helper to get the full routing number based on country
const getFullRoutingNumber = (country, data) => {
    if (country === country_1.OrganizationCountry.Canada) {
        return `${data.transitNumber}${data.institutionNumber}`;
    }
    if (country === country_1.OrganizationCountry.GreatBritain) {
        return data.sortCode;
    }
    if (country === country_1.OrganizationCountry.Australia) {
        return data.bsb;
    }
    // SEPA/IBAN country: Stripe derives the bank from the IBAN, no routing number exists
    if (country === country_1.OrganizationCountry.Ireland || country === country_1.OrganizationCountry.Germany) {
        return "";
    }
    return data.routingNumber;
};
exports.getFullRoutingNumber = getFullRoutingNumber;
const getBankAccountDefaultValues = (country, externalAccount) => {
    if (country === country_1.OrganizationCountry.Canada) {
        return {
            transitNumber: externalAccount?.routingNumber?.split("-")?.[0],
            institutionNumber: externalAccount?.routingNumber?.split("-")?.[1],
        };
    }
    if (country === country_1.OrganizationCountry.GreatBritain) {
        return {
            sortCode: externalAccount?.routingNumber
                ? (0, exports.sanitizeDigitsOnly)(externalAccount.routingNumber)
                : externalAccount?.routingNumber,
        };
    }
    if (country === country_1.OrganizationCountry.Australia) {
        return { bsb: externalAccount?.routingNumber };
    }
    if (country === country_1.OrganizationCountry.Ireland || country === country_1.OrganizationCountry.Germany) {
        return {};
    }
    return { routingNumber: externalAccount?.routingNumber };
};
exports.getBankAccountDefaultValues = getBankAccountDefaultValues;
const getBankAccountFields = (country) => {
    if (country === country_1.OrganizationCountry.GreatBritain) {
        return "sortCode";
    }
    if (country === country_1.OrganizationCountry.Canada) {
        return "transitAndInstitution";
    }
    if (country === country_1.OrganizationCountry.UnitedStates) {
        return "routing";
    }
    if (country === country_1.OrganizationCountry.Australia) {
        return "bsb";
    }
    if (country === country_1.OrganizationCountry.Ireland || country === country_1.OrganizationCountry.Germany) {
        return "iban";
    }
    return null;
};
exports.getBankAccountFields = getBankAccountFields;
const isStripePendingVerificationForCountry = (country) => {
    return country === country_1.OrganizationCountry.Canada;
};
exports.isStripePendingVerificationForCountry = isStripePendingVerificationForCountry;
const hasBankDetectionFlow = (country) => {
    return country === country_1.OrganizationCountry.UnitedStates;
};
exports.hasBankDetectionFlow = hasBankDetectionFlow;
const shouldDisplayBankLinkedSuccessParam = (country) => {
    return country === country_1.OrganizationCountry.UnitedStates;
};
exports.shouldDisplayBankLinkedSuccessParam = shouldDisplayBankLinkedSuccessParam;
